import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { CSSTransition, SwitchTransition } from 'react-transition-group';
import { isEqual } from 'lodash';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';

import { renderPreferencesToaster } from 'components/layouts/Header/Header.component';
import ProgressBar from 'components/ComponentV2/ProgressBar';
import { BasicInfo, DemographicsInfo, useCandidate } from 'hooks/useCandidate';
import { useNotification } from 'hooks/useNotification';
import { FLOW } from 'hooks/useApp';
import { useResponsive } from 'hooks/useResponsive';
import { useCandidateOnboarding } from 'hooks/useCandidateOnboarding';
import {
  EducationHistoryAttributes,
  MATCHING_PREFERENCE_STEPS,
  ONBOARDING_STEPS,
  onboarding_step_object,
} from 'lib/models/candidate';
import { parseArrayResponse, parseResponse } from 'lib/utils/parser';
import { PAGE_ROUTES } from 'lib/page-routes';
import { OPPORTUNITY_TYPE } from 'lib/models/opportunity';
import { useCommonList } from 'lib/contexts/common-list-context';
import { useAuth } from 'lib/providers/AuthProvider';
import { useCandidateMatches } from 'lib/providers/CandidateMatchesProvider';
import { useCandidateDashboard } from 'lib/providers/CandidateDashboardProvider';
import Education from './Education';
import EventType from './EventType';
import Industries from './Industries';
import InternshipType from './InternshipType';
import Languages from './Languages';
import Locations from './Locations';
import OpportunityType from './OpportunityType';
import RemotePreference from './RemotePreference';
import Responsibilities from './Responsibilities';
import VisaPreference from './VisaPreference';
import Demographics from './Demographics';
import RoleLevel from './RoleLevel';
import UploadCV from './UploadCV';
import { SidebarMP } from './SidebarMP/SidebarMP.component';
import MobileHeaderMp from './MobileHeaderMp';

import styles from './Onboarding.module.scss';

type OnboardingProps = {
  isMatchingPreferences?: boolean;
};

export interface Inputs {
  interestable_id: string;
  interestable_type: string;
  weight: number;
}

const Onboarding: React.FC<OnboardingProps> = ({
  isMatchingPreferences = false,
}) => {
  const [t] = useTranslation('candidate-onboarding');
  const screens = useResponsive();
  const notificationInstance = useNotification();
  const router = useRouter();
  const { step: stepFromParam } = router.query;
  const { setIsNewCandidate, candidate } = useAuth();
  const {
    areaOfResponsibilities,
    opportunityTypes,
    internshipKinds,
    eventKinds,
    cityCountryList,
    countries,
    industryOptions,
    languageOptions,
    defaultLanguage,
    roleLevels,
  } = useCommonList();

  const {
    updateCandidateInterests,
    updateCandidateInfo,
    updateCandidateBasicInfo,
    candidateResponsibilities,
    candidateIndustries,
    candidateInterestsMutate,
    deleteCV,
  } = useCandidate();

  const { updateCandidatesLastStep } = useCandidateDashboard();

  const [remote_opportunity, setRemoteOpportunity] = useState(false);

  const routerRedirect = async (step: ONBOARDING_STEPS) => {
    await router.push(
      {
        query: {
          step,
        },
      },
      undefined,
      { shallow: true }
    );
  };

  type QueryParams = {
    step: MATCHING_PREFERENCE_STEPS | ONBOARDING_STEPS;
    matching_preference?: boolean;
  };
  const onStepSelect = (step: MATCHING_PREFERENCE_STEPS | ONBOARDING_STEPS) => {
    const query: Partial<QueryParams> = { step };

    if (isMatchingPreferences) {
      query.matching_preference = isMatchingPreferences;
    }
    router.replace(
      {
        query,
      },
      undefined,
      { shallow: true }
    );
  };

  /** For desktop, we need to add stepFromParam in query param onMount */
  useEffect(() => {
    if ((isMatchingPreferences && screens.sm) || stepFromParam) {
      return;
    }
    onStepSelect(MATCHING_PREFERENCE_STEPS.OPPORTUNITY_TYPE);
  }, [screens, stepFromParam, isMatchingPreferences]);

  const [steps, setSteps] = useState<
    Array<MATCHING_PREFERENCE_STEPS | ONBOARDING_STEPS>
  >([MATCHING_PREFERENCE_STEPS.OPPORTUNITY_TYPE]);
  const totalStep = 10;

  const [showLanguageCount, setShowLanguageCount] = useState<number>();
  const [transitionState, setTransitionState] = useState<
    'left-to-right' | 'right-to-left'
  >('left-to-right');
  const [isLoading, setIsLoading] = useState(false);
  const [isBackLoading, setIsBackLoading] = useState(false);
  const [isNewUserPreference, setNewUserPreference] = useState(false);
  const {
    generateMatchesFromScratch,
    generateNewMatches,
    isPreferenceModified,
    setPreferenceModified,
  } = useCandidateMatches();
  const { saveNotification } = useCandidateOnboarding();

  const {
    opportunity_types: candidateSelectedOpportunities,
    cities: citiesResponse,
    reviewed_preferences,
    touchpointable_kinds: candidateSelectedInternships,
    event_category_ids: candidateSelectedEventIds,
    country_ids: candidateSelectedRemotePreference,
    education_histories: educationHistoriesResponse,
    visa_country_ids: candidateVisaPreference,
    languages: candidateSelectedLanguagesResponse,
    remote_opportunity: candidteRemoteOpportunity,
    experience_preferences: candidateSelectedRoleLevels,
  } = candidate || {};

  useEffect(() => {
    if (
      (candidateSelectedRemotePreference &&
        candidateSelectedRemotePreference.length > 0) ||
      candidteRemoteOpportunity
    ) {
      setRemoteOpportunity(true);
    } else {
      setRemoteOpportunity(false);
    }
  }, [candidateSelectedRemotePreference, candidteRemoteOpportunity]);

  const cities = useMemo(() => {
    return parseArrayResponse(citiesResponse);
  }, [citiesResponse]);

  const candidateLocations = useMemo(() => {
    return cities?.map((city) => city.id);
  }, [cities]);

  const candidateLocationCountries = useMemo(() => {
    /** Taking unique country names from both cities and candidateSelectedRemotePreference*/

    const remotePrefCountries =
      candidateSelectedRemotePreference?.map((id) => {
        const country = countries?.find((country) => country.id === id);
        return { id, name: country?.label || '' };
      }) || [];
    const locationCountries =
      cities?.map((city) => {
        const country = parseResponse(city.country);
        return {
          id: country.id,
          name: country.name,
        };
      }) || [];
    const allCountries = [...remotePrefCountries, ...locationCountries];

    return allCountries.filter(
      (item, index, countries) =>
        countries.findIndex((value) => item.id === value.id) === index
    );
  }, [cities, candidateSelectedRemotePreference, countries]);

  const candidateSelectedLanguages = useMemo(() => {
    const languages = parseArrayResponse(candidateSelectedLanguagesResponse);
    return languages.map((item) => item.id);
  }, [candidateSelectedLanguagesResponse]);

  /** When only remote opportunity is selected
   * Visa pref step should show only those countries
   * which is selected in Remote Preference
   */
  /** Show only candidate selected country list on Visa Pref
   */

  const countryList = useMemo(() => {
    if (remote_opportunity && !cities?.length) {
      return candidateSelectedRemotePreference?.map((id) => {
        const country = countries?.find((item) => item.id === id);
        return {
          id: country?.id || '',
          value: country?.id || '',
          label: country?.label || '',
        };
      });
    } else {
      return candidateLocationCountries?.map((item) => ({
        id: item.id || '',
        value: item?.id || '',
        label: item?.name || '',
      }));
    }
  }, [cities, remote_opportunity, candidateSelectedRemotePreference]);

  const onBackClick = () => {
    setIsLoading(false);
    setIsBackLoading(true);
    setTransitionState('right-to-left');

    const newSteps = steps.slice(0, -1);

    setTimeout(() => {
      setIsBackLoading(false);
    }, 500);
    onStepSelect(
      newSteps[newSteps.length - 1] as
        | MATCHING_PREFERENCE_STEPS
        | ONBOARDING_STEPS
    );
  };

  const isJobSelected =
    candidateSelectedOpportunities?.includes(OPPORTUNITY_TYPE.JOB) || false;
  const isInternshipSelected =
    candidateSelectedOpportunities?.includes(OPPORTUNITY_TYPE.INTERNSHIP) ||
    false;
  const isPartTimeJobSelected =
    candidateSelectedOpportunities?.includes(OPPORTUNITY_TYPE.PART_TIME_JOB) ||
    false;
  const isEventSelected =
    candidateSelectedOpportunities?.includes(OPPORTUNITY_TYPE.EVENT) || false;
  const isEntryOrJuniorLevel = [99, 2].some((value) =>
    candidateSelectedRoleLevels?.includes(value)
  );
  const isMidLevelOrAbove = [3, 4, 5].some((value) =>
    candidateSelectedRoleLevels?.includes(value)
  );

  const isJobOrInternshipOrPartime =
    isJobSelected || isPartTimeJobSelected || isInternshipSelected;
  const isJobOrInternshipOrEvent =
    isJobSelected || isEventSelected || isInternshipSelected;

  const stepsForMP = [
    {
      stepNumber: MATCHING_PREFERENCE_STEPS.OPPORTUNITY_TYPE,
      visible: true,
    },
    {
      stepNumber: MATCHING_PREFERENCE_STEPS.RESPONSIBILITIES,
      visible: isJobOrInternshipOrPartime,
    },
    {
      stepNumber: MATCHING_PREFERENCE_STEPS.ROLE_LEVEL,
      visible: isJobSelected || isPartTimeJobSelected,
    },
    {
      stepNumber: MATCHING_PREFERENCE_STEPS.INTERNSHIP_TYPE,
      visible: isInternshipSelected,
    },
    {
      stepNumber: MATCHING_PREFERENCE_STEPS.EVENT_TYPE,
      visible: isEventSelected,
    },
    {
      stepNumber: MATCHING_PREFERENCE_STEPS.LOCATION,
      visible: true,
    },
    {
      stepNumber: MATCHING_PREFERENCE_STEPS.REMOTE_PREFERENCE,
      visible: remote_opportunity,
    },
    {
      stepNumber: MATCHING_PREFERENCE_STEPS.VISA_PREFERENCE,
      visible: isJobOrInternshipOrPartime,
    },
    {
      stepNumber: MATCHING_PREFERENCE_STEPS.LANGUAGE,
      visible: isJobSelected || isInternshipSelected,
    },
    {
      stepNumber: MATCHING_PREFERENCE_STEPS.INDUSTRIES,
      visible: isJobOrInternshipOrEvent,
    },
  ];

  const stepsForOnboarding = [
    {
      stepNumber: ONBOARDING_STEPS.OPPORTUNITY_TYPE,
      visible: true,
    },
    {
      stepNumber: ONBOARDING_STEPS.UPLOAD_CV,
      visible: true,
    },
    {
      stepNumber: ONBOARDING_STEPS.RESPONSIBILITIES,
      visible: isJobOrInternshipOrPartime,
    },
    {
      stepNumber: ONBOARDING_STEPS.ROLE_LEVEL,
      visible: isJobSelected || isPartTimeJobSelected,
    },
    {
      stepNumber: ONBOARDING_STEPS.INDUSTRIES,
      visible: isJobOrInternshipOrEvent,
    },
    {
      stepNumber: ONBOARDING_STEPS.LOCATION,
      visible: true,
    },
    {
      stepNumber: ONBOARDING_STEPS.REMOTE_PREFERENCE,
      visible:
        remote_opportunity &&
        candidateLocations.length === 0 &&
        isJobOrInternshipOrPartime,
    },
    {
      stepNumber: ONBOARDING_STEPS.LANGUAGE,
      visible: isJobSelected || isInternshipSelected,
    },
    {
      stepNumber: ONBOARDING_STEPS.BASIC_INFO_STANDARD,
      visible:
        (isJobSelected && isEntryOrJuniorLevel) ||
        isInternshipSelected ||
        isEventSelected,
    },
    {
      stepNumber: ONBOARDING_STEPS.BASIC_INFO_SIMPLIFIED,
      visible:
        (!isJobOrInternshipOrEvent && isPartTimeJobSelected) ||
        (isJobSelected &&
          isMidLevelOrAbove &&
          !isInternshipSelected &&
          !isEventSelected &&
          !isEntryOrJuniorLevel),
    },
    {
      stepNumber: ONBOARDING_STEPS.DEMOGRAPHICS,
      visible:
        (isJobSelected && isEntryOrJuniorLevel) ||
        isInternshipSelected ||
        isEventSelected,
    },
  ];
  const getSteps = () => {
    if (!stepFromParam) return;

    const stepsList: Array<{
      stepNumber: MATCHING_PREFERENCE_STEPS | ONBOARDING_STEPS;
      visible: boolean;
    }> = isMatchingPreferences ? stepsForMP : stepsForOnboarding;

    // Find the index of the stepFromParam in the stepsList array
    const endIndex = stepsList.findIndex(
      (step) => step.stepNumber === stepFromParam
    );

    // Filter the steps array from the first index to the found index, including only steps where visible is true
    const newSteps = stepsList
      .slice(0, endIndex + 1)
      .filter((step: { visible: boolean }) => step.visible)
      .map(
        (step: { stepNumber: MATCHING_PREFERENCE_STEPS | ONBOARDING_STEPS }) =>
          step.stepNumber
      );

    setSteps(newSteps);
  };

  useEffect(() => {
    getSteps();
  }, [stepFromParam]);

  const onOpportunityTypeSubmitClick = async ({
    opportunitySelected,
    isBackClicked,
  }: {
    opportunitySelected: Array<string>;
    isBackClicked?: boolean;
  }) => {
    try {
      setIsLoading(true);
      const moveStep = () => {
        setTransitionState('left-to-right');
        setTimeout(() => {
          setIsLoading(false);
          if (isMatchingPreferences) {
            if (isBackClicked) {
              router.replace(PAGE_ROUTES.CANDIDATE_DASHBOARD);
            } else {
              if (screens.sm) {
                onSaveClickMpMobile();
              } else {
                if (
                  opportunitySelected.includes(OPPORTUNITY_TYPE.INTERNSHIP) ||
                  opportunitySelected.includes(OPPORTUNITY_TYPE.JOB) ||
                  opportunitySelected.includes(OPPORTUNITY_TYPE.PART_TIME_JOB)
                ) {
                  onStepSelect(MATCHING_PREFERENCE_STEPS.RESPONSIBILITIES);
                } else if (
                  opportunitySelected.includes(OPPORTUNITY_TYPE.EVENT)
                ) {
                  onStepSelect(MATCHING_PREFERENCE_STEPS.EVENT_TYPE);
                }
              }
            }
          } else {
            routerRedirect(ONBOARDING_STEPS.UPLOAD_CV);
          }
        }, 500);
      };

      const isEqualSelection = isEqual(
        opportunitySelected?.sort(),
        candidateSelectedOpportunities?.sort()
      );
      if (!isEqualSelection) {
        setNewUserPreference(false);
        await updateCandidateInfo({
          partialCandidateAttributes: {
            opportunity_types: opportunitySelected,
          },
        });
        saveNotification();
        setPreferenceModified(true);
      }
      moveStep();
    } catch (error) {
      setIsLoading(false);
      notificationInstance.handleExceptionError(error);
    }
  };

  const onUploadCVNextClick = async (isClicked?: boolean) => {
    if (isClicked) {
      try {
        await deleteCV();
      } catch (error) {
        notificationInstance.handleExceptionError(error);
      }
    }
    setTransitionState('left-to-right');
    setTimeout(() => {
      if (isJobOrInternshipOrPartime) {
        routerRedirect(ONBOARDING_STEPS.RESPONSIBILITIES);
      } else {
        routerRedirect(ONBOARDING_STEPS.INDUSTRIES);
      }
    }, 500);
  };

  const onInternshipKindSubmitClick = async (
    touchpointable_kinds: Array<string>,
    isBackClicked?: boolean
  ) => {
    try {
      if (isBackClicked) {
        setIsBackLoading(true);
      } else setIsLoading(true);

      const moveStep = () => {
        if (isBackClicked) {
          onBackClick();
        } else {
          setTransitionState('left-to-right');
          setTimeout(() => {
            setIsLoading(false);
            if (screens.sm) {
              onSaveClickMpMobile();
            } else if (isEventSelected) {
              onStepSelect(MATCHING_PREFERENCE_STEPS.EVENT_TYPE);
            } else {
              onStepSelect(MATCHING_PREFERENCE_STEPS.LOCATION);
            }
          }, 500);
        }
      };
      const isEqualSelection = isEqual(
        touchpointable_kinds?.sort(),
        candidateSelectedInternships?.sort()
      );
      if (!isEqualSelection) {
        await updateCandidateInfo({
          partialCandidateAttributes: {
            touchpointable_kinds,
          },
        });
        saveNotification();

        setPreferenceModified(true);
      }
      moveStep();
    } catch (error) {
      setIsLoading(false);
      setIsBackLoading(false);
      notificationInstance.handleExceptionError(error);
    }
  };

  const onEventIdSubmitClick = async (
    event_category_ids: Array<string>,
    isBackClicked?: boolean
  ) => {
    try {
      if (isBackClicked) {
        setIsBackLoading(true);
      } else setIsLoading(true);

      const isEqualSelection = isEqual(
        event_category_ids?.sort(),
        candidateSelectedEventIds?.sort()
      );
      const moveStep = () => {
        if (isBackClicked) {
          onBackClick();
        } else {
          setTransitionState('left-to-right');
          setTimeout(() => {
            setIsLoading(false);
            if (screens.sm) {
              onSaveClickMpMobile();
            } else onStepSelect(MATCHING_PREFERENCE_STEPS.LOCATION);
          }, 500);
        }
      };
      if (!isEqualSelection) {
        await updateCandidateInfo({
          partialCandidateAttributes: {
            event_category_ids,
          },
        });
        saveNotification();
        setPreferenceModified(true);
      }
      moveStep();
    } catch (error) {
      setIsLoading(false);
      setIsBackLoading(false);
      notificationInstance.handleExceptionError(error);
    }
  };

  const onResponsibilitiesSubmitClick = async ({
    selectedResponsibilities,
    isBackClicked,
  }: {
    selectedResponsibilities: Array<Inputs>;
    isBackClicked?: boolean;
  }) => {
    try {
      if (isBackClicked) {
        setIsBackLoading(true);
      } else setIsLoading(true);

      const isEqualSelection = isEqual(
        selectedResponsibilities,
        candidateResponsibilities
      );
      const moveStep = () => {
        if (isBackClicked) {
          onBackClick();
        } else {
          setTransitionState('left-to-right');
          setTimeout(() => {
            setIsLoading(false);
            if (isMatchingPreferences) {
              if (screens.sm) {
                onSaveClickMpMobile();
              } else if (isJobSelected || isPartTimeJobSelected) {
                onStepSelect(MATCHING_PREFERENCE_STEPS.ROLE_LEVEL);
              } else if (
                candidateSelectedOpportunities?.includes(
                  OPPORTUNITY_TYPE.INTERNSHIP
                )
              ) {
                onStepSelect(MATCHING_PREFERENCE_STEPS.INTERNSHIP_TYPE);
              } else if (
                candidateSelectedOpportunities?.includes(OPPORTUNITY_TYPE.EVENT)
              ) {
                onStepSelect(MATCHING_PREFERENCE_STEPS.EVENT_TYPE);
              }
            } else {
              if (isJobSelected || isPartTimeJobSelected) {
                routerRedirect(ONBOARDING_STEPS.ROLE_LEVEL);
              } else {
                routerRedirect(ONBOARDING_STEPS.INDUSTRIES);
              }
            }
          }, 500);
        }
      };

      if (!isEqualSelection) {
        const interestResponse = await updateCandidateInterests({
          selectedInterests: selectedResponsibilities,
          interestable_type: 'AreaOfResponsibility',
        });
        saveNotification();

        if (interestResponse) {
          candidateInterestsMutate();
        }
        setPreferenceModified(true);
      }
      moveStep();
    } catch (error) {
      setIsLoading(false);
      setIsBackLoading(false);
      notificationInstance.handleExceptionError(error);
    }
  };

  const onRoleLevelSubmitClick = async ({
    experience_preferences,
    isBackClicked,
  }: {
    experience_preferences: Array<number>;
    isBackClicked?: boolean;
  }) => {
    try {
      if (isBackClicked) {
        setIsBackLoading(true);
      } else setIsLoading(true);

      const moveStep = () => {
        if (isBackClicked) {
          onBackClick();
        } else {
          setTransitionState('left-to-right');
          setTimeout(() => {
            setIsLoading(false);
            if (isMatchingPreferences) {
              if (screens.sm) {
                onSaveClickMpMobile();
              } else if (isInternshipSelected) {
                onStepSelect(MATCHING_PREFERENCE_STEPS.INTERNSHIP_TYPE);
              } else if (
                candidateSelectedOpportunities &&
                candidateSelectedOpportunities.includes(OPPORTUNITY_TYPE.EVENT)
              ) {
                onStepSelect(MATCHING_PREFERENCE_STEPS.EVENT_TYPE);
              } else {
                onStepSelect(MATCHING_PREFERENCE_STEPS.LOCATION);
              }
            } else {
              if (isJobOrInternshipOrEvent)
                routerRedirect(ONBOARDING_STEPS.INDUSTRIES);
              else routerRedirect(ONBOARDING_STEPS.LOCATION);
            }
          }, 500);
        }
      };
      const isEqualSelection = isEqual(
        experience_preferences?.sort(),
        candidateSelectedRoleLevels?.sort()
      );
      if (!isEqualSelection) {
        await updateCandidateInfo({
          partialCandidateAttributes: {
            experience_preferences,
          },
        });
        saveNotification();

        setPreferenceModified(true);
      }
      moveStep();
    } catch (error) {
      setIsLoading(false);
      setIsBackLoading(false);
      notificationInstance.handleExceptionError(error);
    }
  };

  const onIndustriesSubmitClick = async ({
    selectedInterests,
    isBackClicked,
  }: {
    selectedInterests: Array<Inputs>;
    isBackClicked?: boolean;
  }) => {
    try {
      if (isBackClicked) {
        setIsBackLoading(true);
      } else setIsLoading(true);

      const isEqualSelection = isEqual(selectedInterests, candidateIndustries);
      const moveStep = () => {
        if (isBackClicked) {
          onBackClick();
        } else {
          if (!isMatchingPreferences) {
            setTransitionState('left-to-right');
            setTimeout(() => {
              setIsLoading(false);
              routerRedirect(ONBOARDING_STEPS.LOCATION);
            }, 500);
          }
        }
      };
      if (!isEqualSelection) {
        const interestResponse = await updateCandidateInterests({
          selectedInterests,
          interestable_type: 'Industry',
        });
        saveNotification();
        if (interestResponse) {
          candidateInterestsMutate();
        }
        setPreferenceModified(true);
      }
      if (isMatchingPreferences) {
        await updateCandidatesLastStep(FLOW.MATCHING_PREFERENCES);
        if (screens.sm) {
          onSaveClickMpMobile();

          setIsLoading(false);
        } else if (!isBackClicked) {
          await onGenerateNewMatches();
        }
      }
      moveStep();
    } catch (error) {
      setIsLoading(false);
      setIsBackLoading(false);
      notificationInstance.handleExceptionError(error);
    }
  };

  const educationHistories = useMemo(() => {
    const list = parseArrayResponse<EducationHistoryAttributes>(
      educationHistoriesResponse
    )?.filter((item) => item.education_type === 'university');
    return list?.length > 0 ? list[0] : null;
  }, [educationHistoriesResponse]);

  const isEducationDetailChanged = useCallback(
    (data: BasicInfo) => {
      if (!educationHistories) return true;
      const { first_name, last_name, graduation_year } = candidate || {};
      const {
        university: universityResponse,
        subject: subjectResponse,
        degree: degreeResponse,
        university_name,
        degree_name,
      } = educationHistories;

      const university = parseResponse(universityResponse);
      const subject = parseResponse(subjectResponse);
      const degree = parseResponse(degreeResponse);

      if (
        data.first_name === first_name &&
        data.last_name === last_name &&
        (data.university_name === university_name ||
          data.university_id === university?.id) &&
        data.degree_id === degree?.id &&
        data.degree_name === degree_name &&
        data.subject_id === subject?.id &&
        data.graduation_year === graduation_year
      ) {
        return false;
      } else {
        return true;
      }
    },
    [candidate]
  );

  const onEducationSetupClick = async ({
    candidateInfo,
    isBackClicked,
  }: {
    candidateInfo: BasicInfo;
    isBackClicked?: boolean;
  }) => {
    try {
      if (isBackClicked) {
        setIsBackLoading(true);
      } else setIsLoading(true);

      if (isEducationDetailChanged(candidateInfo)) {
        await updateCandidateBasicInfo({
          data: candidateInfo,
        });
      }
      if (isBackClicked) {
        onBackClick();
      } else {
        setTransitionState('left-to-right');
        setTimeout(() => {
          setIsLoading(false);
          routerRedirect(ONBOARDING_STEPS.DEMOGRAPHICS);
        }, 500);
      }
    } catch (error) {
      setIsLoading(false);
      setIsBackLoading(false);
      notificationInstance.handleExceptionError(error);
    }
  };

  const onDemographicSubmitClick = async ({
    candidateInfo,
    isBackClicked,
  }: {
    candidateInfo: DemographicsInfo;
    isBackClicked?: boolean;
  }) => {
    try {
      if (isBackClicked) {
        setIsBackLoading(true);
      } else setIsLoading(true);

      await updateCandidateInfo({
        partialCandidateAttributes: {
          ...candidateInfo,
          onboarding_completed: !isBackClicked ? true : undefined,
        },
      });
      if (isBackClicked) {
        setTransitionState('right-to-left');
        onBackClick();
      } else {
        await updateCandidatesLastStep(FLOW.ONBOARDING);
        await generateMatchesFromScratch();
        router.push(
          `${PAGE_ROUTES.CANDIDATE_DASHBOARD}?showWelcomePopup=true`,
          PAGE_ROUTES.CANDIDATE_DASHBOARD
        );
      }
    } catch (error) {
      setIsBackLoading(false);
      notificationInstance.handleExceptionError(error);
    } finally {
      setIsNewCandidate(false);
      setIsLoading(false);
    }
  };

  const onLocationsSubmitClick = async (
    isRemoteSelected: boolean,
    locationsSelected: Array<string>,
    isBackClicked?: boolean
  ) => {
    try {
      if (isBackClicked) {
        setIsBackLoading(true);
      } else setIsLoading(true);

      const isEqualSelection =
        isEqual(locationsSelected?.sort(), candidateLocations?.sort()) &&
        isRemoteSelected === remote_opportunity;

      const moveStep = () => {
        if (isBackClicked) {
          if (isRemoteSelected) {
            setRemoteOpportunity(true);
          } else {
            setRemoteOpportunity(false);
          }
          onBackClick();
        } else {
          setTransitionState('left-to-right');
          setTimeout(() => {
            setIsLoading(false);
            if (isRemoteSelected) {
              setRemoteOpportunity(true);
              if (isMatchingPreferences) {
                if (
                  locationsSelected.length === 0 &&
                  isJobOrInternshipOrPartime
                ) {
                  onStepSelect(MATCHING_PREFERENCE_STEPS.REMOTE_PREFERENCE);
                } else {
                  if (isJobOrInternshipOrPartime) {
                    onStepSelect(MATCHING_PREFERENCE_STEPS.VISA_PREFERENCE);
                  } else if (screens.sm) {
                    onSaveClickMpMobile();
                  } else {
                    onStepSelect(MATCHING_PREFERENCE_STEPS.INDUSTRIES);
                  }
                }
              } else if (
                locationsSelected.length === 0 &&
                isJobOrInternshipOrPartime
              ) {
                routerRedirect(ONBOARDING_STEPS.REMOTE_PREFERENCE);
              } else if (
                locationsSelected.length > 0 &&
                (isJobSelected || isInternshipSelected)
              ) {
                routerRedirect(ONBOARDING_STEPS.LANGUAGE);
              } else {
                if (!isJobOrInternshipOrEvent)
                  routerRedirect(ONBOARDING_STEPS.BASIC_INFO_SIMPLIFIED);
                else routerRedirect(ONBOARDING_STEPS.BASIC_INFO_STANDARD);
              }
            } else {
              setRemoteOpportunity(false);
              if (isMatchingPreferences) {
                if (isJobOrInternshipOrPartime) {
                  onStepSelect(MATCHING_PREFERENCE_STEPS.VISA_PREFERENCE);
                } else if (screens.sm) {
                  onSaveClickMpMobile();
                } else {
                  onStepSelect(MATCHING_PREFERENCE_STEPS.INDUSTRIES);
                }
              } else {
                if (isJobSelected || isInternshipSelected)
                  routerRedirect(ONBOARDING_STEPS.LANGUAGE);
                else if (!isJobOrInternshipOrEvent)
                  routerRedirect(ONBOARDING_STEPS.BASIC_INFO_SIMPLIFIED);
                else routerRedirect(ONBOARDING_STEPS.BASIC_INFO_STANDARD);
              }
            }
          }, 500);
        }
      };
      if (!isEqualSelection) {
        const filteredCountryIds = cityCountryList
          .filter((city) => locationsSelected.includes(city.id))
          .map((city) => city.country_id);
        const uniqueCountryIds = filteredCountryIds.filter(
          (item, index) => filteredCountryIds.indexOf(item) === index
        );
        await updateCandidateInfo({
          partialCandidateAttributes: {
            city_ids: locationsSelected,
            remote_opportunity: isRemoteSelected,
            country_ids: isRemoteSelected ? uniqueCountryIds : [],
          },
        });
        saveNotification();

        setPreferenceModified(true);
      }
      moveStep();
    } catch (error) {
      setIsLoading(false);
      setIsBackLoading(false);
      notificationInstance.handleExceptionError(error);
    }
  };

  const onRemotePreferenceSubmitClick = async (
    countrySelected: Array<string>,
    isBackClicked?: boolean
  ) => {
    try {
      if (isBackClicked) {
        setIsBackLoading(true);
      } else setIsLoading(true);

      const moveStep = () => {
        if (isBackClicked) {
          onBackClick();
        } else {
          setTransitionState('left-to-right');
          setTimeout(() => {
            setIsLoading(false);
            if (isMatchingPreferences) {
              if (isJobOrInternshipOrPartime) {
                onStepSelect(MATCHING_PREFERENCE_STEPS.VISA_PREFERENCE);
              } else if (screens.sm) {
                onSaveClickMpMobile();
              } else {
                onStepSelect(MATCHING_PREFERENCE_STEPS.INDUSTRIES);
              }
            } else {
              if (isJobSelected || isInternshipSelected)
                routerRedirect(ONBOARDING_STEPS.LANGUAGE);
              else {
                if (!isJobOrInternshipOrEvent && isPartTimeJobSelected)
                  routerRedirect(ONBOARDING_STEPS.BASIC_INFO_SIMPLIFIED);
                else routerRedirect(ONBOARDING_STEPS.BASIC_INFO_STANDARD);
              }
            }
          }, 500);
        }
      };

      const isEqualSelection = isEqual(
        countrySelected?.sort(),
        candidateSelectedRemotePreference?.sort()
      );
      if (!isEqualSelection) {
        await updateCandidateInfo({
          partialCandidateAttributes: {
            country_ids: countrySelected,
          },
        });
        saveNotification();

        setPreferenceModified(true);
      }
      moveStep();
    } catch (error) {
      setIsLoading(false);
      setIsBackLoading(false);
      notificationInstance.handleExceptionError(error);
    }
  };

  const onVisaPreferenceSubmitClick = async (
    visa_country_ids: Array<string>,
    isBackClicked?: boolean
  ) => {
    try {
      if (isBackClicked) {
        setIsBackLoading(true);
      } else setIsLoading(true);
      const isEqualSelection = isEqual(
        visa_country_ids?.sort(),
        candidateVisaPreference?.sort()
      );
      const moveStep = async () => {
        if (isBackClicked) {
          onBackClick();
        } else if (
          !isJobSelected &&
          !isInternshipSelected &&
          !isEventSelected
        ) {
          await updateCandidatesLastStep(FLOW.MATCHING_PREFERENCES);
          if (screens.sm) {
            onSaveClickMpMobile();

            setIsLoading(false);
          } else if (!isBackClicked) {
            await onGenerateNewMatches();
          }
        } else {
          setTransitionState('left-to-right');
          setTimeout(() => {
            setIsLoading(false);
            if (screens.sm) {
              onSaveClickMpMobile();
            } else if (isJobSelected || isInternshipSelected) {
              onStepSelect(MATCHING_PREFERENCE_STEPS.LANGUAGE);
            } else onStepSelect(MATCHING_PREFERENCE_STEPS.INDUSTRIES);
          }, 500);
        }
      };

      if (!isEqualSelection) {
        await updateCandidateInfo({
          partialCandidateAttributes: {
            visa_country_ids,
          },
        });
        saveNotification();

        setPreferenceModified(true);
      }
      moveStep();
    } catch (error) {
      setIsLoading(false);
      setIsBackLoading(false);
      notificationInstance.handleExceptionError(error);
    }
  };

  const onLanguageSubmitClick = async (
    language_ids: Array<string>,
    isBackClicked?: boolean,
    showCount?: number
  ) => {
    try {
      if (isBackClicked) {
        setIsBackLoading(true);
      } else setIsLoading(true);

      const moveStep = () => {
        if (isBackClicked) {
          onBackClick();
        } else {
          setTransitionState('left-to-right');
          setTimeout(() => {
            setIsLoading(false);
            if (isMatchingPreferences) {
              if (screens.sm) {
                onSaveClickMpMobile();
              } else onStepSelect(MATCHING_PREFERENCE_STEPS.INDUSTRIES);
            } else {
              if (
                !isJobOrInternshipOrEvent ||
                (isJobSelected &&
                  isMidLevelOrAbove &&
                  !isInternshipSelected &&
                  !isEventSelected &&
                  !isEntryOrJuniorLevel)
              )
                routerRedirect(ONBOARDING_STEPS.BASIC_INFO_SIMPLIFIED);
              else routerRedirect(ONBOARDING_STEPS.BASIC_INFO_STANDARD);
            }
          }, 500);
        }
      };

      const isEqualSelection = isEqual(
        language_ids?.sort(),
        candidateSelectedLanguages?.sort()
      );
      if (!isEqualSelection) {
        await updateCandidateInfo({
          partialCandidateAttributes: {
            language_ids,
          },
        });
        saveNotification();
        setPreferenceModified(true);
      }
      setShowLanguageCount(showCount || 15);
      moveStep();
    } catch (error) {
      setIsLoading(false);
      setIsBackLoading(false);
      notificationInstance.handleExceptionError(error);
    }
  };

  const onGenerateNewMatches = async () => {
    setIsLoading(true);
    router.push(PAGE_ROUTES.CANDIDATE_DASHBOARD);
    try {
      await generateNewMatches();
      setIsLoading(false);

      if (screens.sm) {
        setPreferenceModified(false);
      }
    } catch (error) {
      console.error();
    }
  };

  const matchingPreferencesQuestion = () => {
    switch (stepFromParam) {
      case MATCHING_PREFERENCE_STEPS.OPPORTUNITY_TYPE:
        return (
          <>
            {isNewUserPreference
              ? notificationInstance.custom({
                  message: '',
                  timeout: 10000,
                  renderContent: renderPreferencesToaster,
                })
              : null}
            <OpportunityType
              opportunityTypes={opportunityTypes}
              candidateSelectedOpportunities={
                candidateSelectedOpportunities || []
              }
              onNextClick={onOpportunityTypeSubmitClick}
              isLoading={isLoading}
              isMatchingPreferences={true}
              onStepSelect={onStepSelect}
            />
          </>
        );
      case MATCHING_PREFERENCE_STEPS.RESPONSIBILITIES:
        return (
          <Responsibilities
            candidateResponsibilities={candidateResponsibilities}
            areaOfResponsibilities={areaOfResponsibilities}
            onNextClick={onResponsibilitiesSubmitClick}
            isLoading={isLoading}
            isBackLoading={isBackLoading}
            isMatchingPreferences={true}
            onStepSelect={onStepSelect}
          />
        );
      case MATCHING_PREFERENCE_STEPS.ROLE_LEVEL:
        return (
          <RoleLevel
            roleLevels={roleLevels}
            candidateSelectedLevel={candidateSelectedRoleLevels || []}
            onNextClick={onRoleLevelSubmitClick}
            isLoading={isLoading}
            isBackLoading={isBackLoading}
            isSquareCheckbox={true}
            isMatchingPreferences={true}
            onStepSelect={onStepSelect}
          />
        );
      case MATCHING_PREFERENCE_STEPS.INTERNSHIP_TYPE:
        return (
          <InternshipType
            internshipKinds={internshipKinds}
            candidateSelectedInternships={candidateSelectedInternships || []}
            onNextClick={onInternshipKindSubmitClick}
            isLoading={isLoading}
            isBackLoading={isBackLoading}
            onStepSelect={onStepSelect}
          />
        );
      case MATCHING_PREFERENCE_STEPS.EVENT_TYPE:
        return (
          <EventType
            eventKinds={eventKinds}
            candidateSelectedIds={candidateSelectedEventIds || []}
            onNextClick={onEventIdSubmitClick}
            isLoading={isLoading}
            isBackLoading={isBackLoading}
            onStepSelect={onStepSelect}
          />
        );

      case MATCHING_PREFERENCE_STEPS.LOCATION:
        return (
          <Locations
            cityCountryList={cityCountryList}
            countries={countries}
            candidateLocations={candidateLocations}
            remote_opportunity={remote_opportunity}
            onNextClick={onLocationsSubmitClick}
            isLoading={isLoading}
            isBackLoading={isBackLoading}
            isMatchingPreferences={true}
            onStepSelect={onStepSelect}
          />
        );
      case MATCHING_PREFERENCE_STEPS.REMOTE_PREFERENCE:
        return (
          <RemotePreference
            candidateChoice={candidateSelectedRemotePreference || []}
            countries={countries}
            onNextClick={onRemotePreferenceSubmitClick}
            isLoading={isLoading}
            isBackLoading={isBackLoading}
            isMatchingPreferences={true}
            onStepSelect={onStepSelect}
          />
        );
      case MATCHING_PREFERENCE_STEPS.VISA_PREFERENCE:
        return (
          <VisaPreference
            candidateChoice={candidateVisaPreference || []}
            candidateLocationCountries={candidateLocationCountries}
            visaCountries={countryList}
            allCountries={countries}
            onNextClick={onVisaPreferenceSubmitClick}
            isLoading={isLoading}
            isBackLoading={isBackLoading}
            onStepSelect={onStepSelect}
          />
        );
      case MATCHING_PREFERENCE_STEPS.LANGUAGE:
        return (
          <Languages
            candidateChoice={candidateSelectedLanguages}
            showLanguageCount={showLanguageCount}
            languages={languageOptions}
            onNextClick={onLanguageSubmitClick}
            isLoading={isLoading}
            isBackLoading={isBackLoading}
            defaultLanguage={defaultLanguage}
            isMatchingPreferences={true}
            onStepSelect={onStepSelect}
          />
        );
      case MATCHING_PREFERENCE_STEPS.INDUSTRIES:
        return (
          <Industries
            candidateIndustries={candidateIndustries}
            industries={industryOptions}
            onNextClick={onIndustriesSubmitClick}
            isLoading={isLoading}
            isBackLoading={isBackLoading}
            isMatchingPreferences={true}
            onStepSelect={onStepSelect}
          />
        );
      default:
    }
  };

  const { progressBarStep } = useMemo(() => {
    const last_step = steps[steps.length - 1];
    return {
      progressBarStep: onboarding_step_object[last_step as ONBOARDING_STEPS],
    };
  }, [steps]);

  const question = () => {
    switch (stepFromParam) {
      case ONBOARDING_STEPS.OPPORTUNITY_TYPE:
        return (
          <OpportunityType
            candidateSelectedOpportunities={
              candidateSelectedOpportunities || []
            }
            onNextClick={onOpportunityTypeSubmitClick}
            isLoading={isLoading}
            isMatchingPreferences={false}
            isCandidateOnboardingPage={true}
            opportunityTypes={opportunityTypes}
          />
        );
      case ONBOARDING_STEPS.UPLOAD_CV:
        return <UploadCV onNextClick={onUploadCVNextClick} />;
      case ONBOARDING_STEPS.RESPONSIBILITIES:
        return (
          <Responsibilities
            candidateResponsibilities={candidateResponsibilities}
            areaOfResponsibilities={areaOfResponsibilities}
            onNextClick={onResponsibilitiesSubmitClick}
            isLoading={isLoading}
            isBackLoading={isBackLoading}
            isCandidateOnboardingPage={true}
          />
        );
      case ONBOARDING_STEPS.ROLE_LEVEL:
        return (
          <RoleLevel
            roleLevels={roleLevels}
            candidateSelectedLevel={candidateSelectedRoleLevels || []}
            onNextClick={onRoleLevelSubmitClick}
            isLoading={isLoading}
            isBackLoading={isBackLoading}
            isSquareCheckbox={true}
          />
        );
      case ONBOARDING_STEPS.INDUSTRIES:
        return (
          <Industries
            candidateIndustries={candidateIndustries}
            industries={industryOptions}
            onNextClick={onIndustriesSubmitClick}
            isLoading={isLoading}
            isBackLoading={isBackLoading}
            isMatchingPreferences={false}
          />
        );
      case ONBOARDING_STEPS.LOCATION:
        return (
          <Locations
            cityCountryList={cityCountryList}
            countries={countries}
            candidateLocations={candidateLocations}
            remote_opportunity={remote_opportunity}
            onNextClick={onLocationsSubmitClick}
            isLoading={isLoading}
            isBackLoading={isBackLoading}
            isMatchingPreferences={false}
          />
        );
      case ONBOARDING_STEPS.REMOTE_PREFERENCE:
        return (
          <RemotePreference
            candidateChoice={candidateSelectedRemotePreference || []}
            countries={countries}
            onNextClick={onRemotePreferenceSubmitClick}
            isLoading={isLoading}
            isBackLoading={isBackLoading}
            isMatchingPreferences={false}
          />
        );
      case ONBOARDING_STEPS.LANGUAGE:
        return (
          <Languages
            candidateChoice={candidateSelectedLanguages}
            showLanguageCount={showLanguageCount}
            languages={languageOptions}
            onNextClick={onLanguageSubmitClick}
            isLoading={isLoading}
            isBackLoading={isBackLoading}
            defaultLanguage={defaultLanguage}
          />
        );
      case ONBOARDING_STEPS.BASIC_INFO_STANDARD:
        return (
          <Education
            onNextClick={onEducationSetupClick}
            isLoading={isLoading}
            isBackLoading={isBackLoading}
          />
        );
      case ONBOARDING_STEPS.BASIC_INFO_SIMPLIFIED:
        return (
          <Demographics
            onNextClick={onDemographicSubmitClick}
            isLoading={isLoading}
            isBackLoading={isBackLoading}
            isSimplifiedVersion={true}
          />
        );
      case ONBOARDING_STEPS.DEMOGRAPHICS:
        return (
          <Demographics
            onNextClick={onDemographicSubmitClick}
            isLoading={isLoading}
            isBackLoading={isBackLoading}
          />
        );
      default:
    }
  };

  const onSaveClickMpMobile = () => {
    if (stepFromParam) {
      router.replace(
        {
          query: {
            matching_preference: isMatchingPreferences,
          },
        },
        undefined,
        { shallow: true }
      );
    } else {
      router.replace(PAGE_ROUTES.CANDIDATE_DASHBOARD);
    }
  };

  useEffect(() => {
    const updateReviewedPreferences = async () => {
      try {
        await updateCandidateInfo({
          partialCandidateAttributes: {
            reviewed_preferences: true,
          },
        });
        setNewUserPreference(false);
      } catch (error) {
        console.error(error);
      }
    };

    if (isMatchingPreferences && reviewed_preferences === false) {
      setNewUserPreference(true);
      updateReviewedPreferences();
    }
  }, [isMatchingPreferences, reviewed_preferences]);

  return (
    <div className={styles['page-content']}>
      {!stepFromParam ? (
        <MobileHeaderMp
          title={t('matching-preferences.title')}
          onChevronClick={onSaveClickMpMobile}
        />
      ) : null}
      {/* Removing transitioning for MP, as now we have sidebar to navigate */}
      {isMatchingPreferences || !stepFromParam ? null : (
        <>
          <ProgressBar
            percentCompleted={(progressBarStep / totalStep) * 100}
            color="Primary"
            showProgressWhen0={false}
            className={styles.progressBarHeader}
          />
          <SwitchTransition mode="out-in">
            <CSSTransition
              key={stepFromParam?.toString()}
              addEndListener={(node, done) => {
                node.addEventListener('transitionend', done, false);
              }}
              classNames={transitionState}
            >
              <>
                {isMatchingPreferences
                  ? matchingPreferencesQuestion()
                  : question()}
              </>
            </CSSTransition>
          </SwitchTransition>
        </>
      )}
      {isMatchingPreferences ? (
        stepFromParam || !screens.sm ? (
          matchingPreferencesQuestion()
        ) : (
          <SidebarMP
            onStepSelect={onStepSelect}
            isReady={isPreferenceModified}
            onGenerateNewMatches={onGenerateNewMatches}
            isLoading={isLoading}
          />
        )
      ) : null}
    </div>
  );
};

export default Onboarding;
